import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, MessageCircle, Loader2, Sparkles } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Where can we have dinner?",
  "How do I turn on the hot tub?",
  "Where are the remotes?",
  "Where can we access the lake?",
  "Where can we rent kayaks or a boat?",
  "What do we do at checkout?",
];

export default function Concierge({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const listRef = useRef(null);

  useEffect(() => { listRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [messages, busy]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function ask(msg) {
    const text = (msg ?? input).trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const res = await http.post("/api/concierge/chat", { message: text, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (err) {
      const detail = err.response?.data?.detail || "The concierge is temporarily unavailable. Please try again or text (210) 274-3556.";
      setMessages((m) => [...m, { role: "assistant", content: detail, error: true }]);
      toast.error("Concierge error");
    } finally { setBusy(false); }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="concierge-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Grandview Concierge"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-[#0E2A47]/50 backdrop-blur-md flex items-end sm:items-center justify-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
            className="w-full max-w-lg h-[85vh] sm:h-[640px] bg-[#FAFAF8] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#0E2A47]/8 bg-white">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#1FB6C9]/10 flex items-center justify-center">
                  <Sparkles size={18} className="text-[#1FB6C9]" />
                </div>
                <div>
                  <div className="font-display font-semibold text-[#0E2A47]">Grandview Concierge</div>
                  <div className="text-xs text-[#0E2A47]/50 font-body">Powered by AI · Answers grounded in this guide</div>
                </div>
              </div>
              <button data-testid="concierge-close" onClick={onClose} className="p-2 rounded-full hover:bg-[#F1E8DA]/60"><X size={18} /></button>
            </div>

            <div ref={listRef} data-testid="concierge-messages" className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <p className="text-[#0E2A47]/70 font-body">
                    Hi — I'm your Grandview Concierge. Ask me anything about the property, amenities, checkout, or local ideas.
                  </p>
                  <div className="grid gap-2">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={s}
                        data-testid={`concierge-suggestion-${i}`}
                        onClick={() => ask(s)}
                        className="text-left rounded-lg border border-[#0E2A47]/10 bg-white px-4 py-3 text-sm font-body text-[#0E2A47] hover:border-[#1FB6C9] hover:bg-[#1FB6C9]/5 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} data-testid={`concierge-msg-${m.role}-${i}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div className={`max-w-[85%] px-4 py-3 rounded-2xl font-body text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#1FB6C9] text-white rounded-br-md" : m.error ? "bg-red-50 text-red-800 rounded-bl-md border border-red-100" : "bg-white text-[#0E2A47] rounded-bl-md border border-[#0E2A47]/8"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start"><div className="bg-white border border-[#0E2A47]/8 rounded-2xl rounded-bl-md px-4 py-3"><Loader2 size={16} className="animate-spin text-[#1FB6C9]" /></div></div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); ask(); }}
              className="p-4 border-t border-[#0E2A47]/8 bg-white flex items-center gap-3"
            >
              <input
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about Wi-Fi, hot tub, dinner…"
                className="flex-1 rounded-full border border-[#0E2A47]/15 bg-[#FAFAF8] px-4 py-3 text-sm font-body text-[#0E2A47] outline-none focus:border-[#1FB6C9]"
              />
              <button data-testid="concierge-send" type="submit" disabled={busy || !input.trim()} className="h-11 w-11 flex items-center justify-center rounded-full bg-[#1FB6C9] text-white disabled:opacity-40">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
